import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import StepWizard from "react-step-wizard";
import { 
  StepBooleanQuestion, 
  StepPasswordCreation, 
  StepVerification, 
  StepSuccess 
} from "../Shared/SmartIdWizardSteps";
import { SmartIdWizardErrorHandler } from "../Shared/SmartIdWizardErrorHandler";
import { useSmartIdSessionPolling } from "../Shared/useSmartIdSessionPolling";
import { SMART_ID_POLLING_RESULT } from "../Shared/smartIdConstants";
import { NO_TRANSITIONS } from "components/tools/wizards/wizardTransitions";

export function SmartIdRemoveWizard(props) {
  const [wizard, setWizard] = useState(null);
  const history = useHistory();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  const {
    pin,
    sessionPollingError,
    isAuthenticating,
    startUserIdSession,
    pollUserIdSession,
    removeSmartId
  } = useSmartIdSessionPolling();

  const handleErrorNavigation = () => {
    if (props.onNavigateToProfile) {
      props.onNavigateToProfile();
    } else {
      history.push('/profileInformation');
    }
  };

  const onYesClicked = async () => {
    const hasPassword = props.initialData?.isPasswordAdded;
    if (!hasPassword) {
      wizard.nextStep(); // Go to Step 2: Password Creation
    } else {
      wizard.goToStep(3); // Go to Step 3: Smart-ID Authentication
      await startSmartIdAuth();
    }
  };

  const onPasswordSuccess = async () => {
    wizard.nextStep(); // Go to Step 3: Smart-ID Authentication
    await startSmartIdAuth();
  };

  const startSmartIdAuth = async () => {
    const success = await startUserIdSession();
    if (success) {
      await startPollingSessionStep();
    }
  };

  const startPollingSessionStep = async () => {
    if (!isMountedRef.current) {
      console.debug("SmartIdRemoveWizard: Component unmounted, stopping polling");
      return;
    }

    const result = await pollUserIdSession();
    
    if (result === SMART_ID_POLLING_RESULT.SUCCESS) {
      await removeSmartIdAndProceed();
    } else if (result === SMART_ID_POLLING_RESULT.CONTINUE) {
      await startPollingSessionStep(); // Continue polling
    }
    // For "expired" or "error", the hook already sets sessionPollingError
  };

  const removeSmartIdAndProceed = async () => {
    const success = await removeSmartId();
    if (success) {
      wizard.nextStep(); // Go to Step 4: Success
    }
  };
  
  return (
    <SmartIdWizardErrorHandler 
      error={sessionPollingError} 
      onPrimaryButtonClick={handleErrorNavigation}
      buttonTextKey="card_replacement_from_profile_page_return_btn"
    >
      <StepWizard instance={setWizard} transitions={NO_TRANSITIONS}>
        <StepBooleanQuestion onYesClicked={onYesClicked} />
        <StepPasswordCreation onPasswordSuccess={onPasswordSuccess} />
        <StepVerification 
          pin={pin}
          isWaiting={isAuthenticating}
        />
        <StepSuccess 
          onNavigateToProfile={props.onNavigateToProfile}
          messageKey="smart_id_remove_wizard_info_page_message"
        />
      </StepWizard>
    </SmartIdWizardErrorHandler>
  );
}